import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import {
  Card,
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../components";
import { useApolloQuery } from "../../../../hooks";
import { gql, useMutation } from "@apollo/client";
import { DownArrowIcon } from "../../../../@lib/icons";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toastHandler } from "../../../../utils/helpers";
import { statusCode, toastTypesKeys } from "../../../../utils/constants";
import { ToastContainer } from "react-toastify";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import { ConfirmationDialogs } from "../features";

const noPendingPolicyDocument = gql`
  query ($limit: Int, $page: Int) {
    noPendingPolicies(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        geographicalArea {
          name
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policyCategory {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const noPendingPracticesDocument = gql`
  query ($limit: Int, $page: Int) {
    noPendingPractices(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policy {
          title
        }
        practiceCategory {
          name
        }
        practiceType {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const noPendingPeopleDocument = gql`
  query ($limit: Int, $page: Int) {
    noPendingPeople(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        firstname
        id
        lastname
        picture {
          source
          originalFilename
        }
        status
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;
const policyStatusUpdate = gql`
  mutation UpdatePolicyStatus($policyInput: PolicyStatusUpdateInput!) {
    policyStatusUpdate(input: $policyInput) {
      clientMutationId
      policy {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        geographicalArea {
          name
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policyCategory {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
    }
  }
`;

const practiceStatusUpdate = gql`
  mutation UpdatePracticeStatus($practiceInput: PracticeStatusUpdateInput!) {
    practiceStatusUpdate(input: $practiceInput) {
      clientMutationId
      practice {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policy {
          title
        }
        practiceCategory {
          name
        }
        practiceType {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
    }
  }
`;

const peopleStatusUpdate = gql`
  mutation UpdatePersonStatus($personInput: PersonStatusUpdateInput!) {
    personStatusUpdate(input: $personInput) {
      clientMutationId
      person {
        country {
          name
        }
        createdAt
        firstname
        id
        lastname
        picture {
          source
          originalFilename
        }
        status
        title
        updatedAt
      }
    }
  }
`;

const moduleIDKeys = {
  policy: 1,
  practice: 2,
  people: 3,
};

function isModule(location: any) {
  const isPractices = location?.pathname?.includes("practices") || false;
  const isPolicies = location?.pathname?.includes("policies") || false;

  if (isPolicies) {
    return {
      queryDocument: noPendingPolicyDocument,
      createPath: "/admin/policies/new",
      heading: "Policies",
      modalModuleName: "policy",
      statusUpdateGQL: policyStatusUpdate,
      moduleID: moduleIDKeys.policy,
      viewPath: "/admin/policies/",
    };
  } else if (isPractices) {
    return {
      queryDocument: noPendingPracticesDocument,
      createPath: "/admin/practices/new",
      heading: "Practices",
      modalModuleName: "practice",
      statusUpdateGQL: practiceStatusUpdate,
      moduleID: moduleIDKeys.practice,
      viewPath: "/admin/practices/",
    };
  } else {
    return {
      queryDocument: noPendingPeopleDocument,
      createPath: "/admin/people/new",
      heading: "People",
      modalModuleName: "people",
      statusUpdateGQL: peopleStatusUpdate,
      moduleID: moduleIDKeys.people,
      viewPath: "/admin/person_detail?id=",
    };
  }
}

function AdminViewAll() {
  const [limit, setLimit] = useState(8);
  const [rejectReason, setRejectReason] = useState("");
  const [isOpen, setIsOpen] = useState<any>({
    status: false,
    currId: null,
    moduleCode: 2,
  });
  const [isError, setIsError] = useState(false);

  const location = useLocation();
  const { translator } = useLanguageTranslator();
  const { user } = useAuthSession() as any;
  const isPeople = location?.pathname?.includes("people") || false;

  const {
    queryDocument,
    createPath,
    heading,
    modalModuleName,
    statusUpdateGQL,
    moduleID,
    viewPath,
  }: any = isModule(location);

  const [statusUpdate] = useMutation(statusUpdateGQL, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const {
    data: noPending,
    loading,
    refetch,
  } = useApolloQuery({
    queryDocument: queryDocument,
    isPrivateCall: true,
    variables: {
      limit: limit,
    },
  });

  function onClickHandler() {
    const scrollPosition = window.scrollY;
    setLimit(limit + 8);

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 1000);
  }

  const data =
    noPending?.noPendingPolicies?.collection ||
    noPending?.noPendingPractices?.collection ||
    noPending?.noPendingPeople?.collection ||
    [];

  const metadata =
    noPending?.noPendingPolicies?.metadata ||
    noPending?.noPendingPractices?.metadata ||
    noPending?.noPendingPeople?.metadata;

  const hasMore =
    limit === metadata?.totalCount ||
    limit > metadata?.totalCount ||
    !metadata?.totalCount;

  function onAdminActions(id: any, type: any) {
    setIsOpen({ status: true, currId: id, moduleCode: type });
  }

  async function onUpdateClickHandler(id: number) {
    if (isOpen?.moduleCode === statusCode.approved) {
      try {
        const response = await statusUpdate({
          variables:
            moduleID === moduleIDKeys?.policy
              ? { policyInput: { id: Number(id), status: "Approved" } }
              : moduleID === moduleIDKeys?.practice
              ? { practiceInput: { id: Number(id), status: "Approved" } }
              : {
                  personInput: { id: Number(id), status: "Approved" },
                },
        });
        if (response?.data) {
          setIsOpen({ status: false, currId: null, module: "" });
          toastHandler("Status is Updated", toastTypesKeys.success);
          setTimeout(() => {
            refetch();
          }, 2300);
        }
      } catch (error) {
        console.error("Error:", error);
        setIsOpen({ status: false, currId: null, module: "" });
        setIsError(true);
      }
      return;
    }

    if (!rejectReason)
      return toastHandler("Please enter the Reason", toastTypesKeys.error);

    try {
      const response = await statusUpdate({
        variables:
          moduleID === moduleIDKeys?.policy
            ? {
                policyInput: {
                  id: Number(id),
                  status: "Rejected",
                  reason: rejectReason,
                },
              }
            : moduleID === moduleIDKeys?.practice
            ? {
                practiceInput: {
                  id: Number(id),
                  status: "Rejected",
                  reason: rejectReason,
                },
              }
            : {
                personInput: {
                  id: Number(id),
                  status: "Rejected",
                  reason: rejectReason,
                },
              },
      });
      if (response?.data) {
        setIsOpen({ status: false, currId: null, module: "" });
        toastHandler("Status is Updated", toastTypesKeys.success);
        setTimeout(() => {
          refetch();
        }, 2300);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsOpen({ status: false, currId: null, module: "" });
      setRejectReason("");
      setIsError(true);
    }
  }

  return (
    <>
      <ToastContainer />
      <ConfirmationDialogs
        text={modalModuleName}
        viewPath={`${viewPath}${isOpen?.currId}`}
        isOpen={isOpen}
        onClickHandler={onUpdateClickHandler}
        setIsOpen={setIsOpen}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        isError={isError}
        setIsError={setIsError}
      />
      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 2, mb: 2 }}>
          <Stack
            direction={{ lg: "row" }}
            justifyContent={{ lg: "space-between" }}
            alignItems={{ lg: "center" }}
          >
            <Typography variant="h4">
              {translator("status.Approved")} & {translator("status.Rejected")}{" "}
              {heading}
            </Typography>
            <Box>
              <Button
                href={createPath}
                sx={{
                  px: 6,
                  backgroundColor: "#F6A000",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "primary.light",
                  },
                }}
              >
                {translator("generic.CREATE")}
              </Button>
            </Box>
          </Stack>
        </Paper>
        <Grid container spacing={2}>
          {data?.map((p: any, i: any) => (
            <Grid key={`${i}`} item xs={12} md={6} lg={3}>
              <Slide in timeout={180 * (i + 1)} direction="right">
                <Card
                  {...p}
                  path={
                    isPeople ? `${viewPath}${p?.id}` : `${viewPath}${p?.id}`
                  }
                  sx={{ height: "100%" }}
                  isAdmin={true}
                  onAdminActions={onAdminActions}
                />
              </Slide>
            </Grid>
          ))}

          {/* {loading &&
            policies.length === 0 &&
            Array(4)
              .fill(null)
              .map((_, i) => (
                <Fade key={`${i}`} in timeout={180 * (i + 1)}>
                  <Grid item xs={12} md={6} lg={3}>
                    {CardSkeleton}
                  </Grid>
                </Fade>
              ))} */}
        </Grid>
        <Paper>
          <ConditionalRenderer condition={!hasMore}>
            <div
              style={{
                margin: "10px 0px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button style={{ color: "#979da7" }} onClick={onClickHandler}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {translator("Show more")} <DownArrowIcon />
                </div>
              </Button>
            </div>
          </ConditionalRenderer>
        </Paper>
      </Container>
    </>
  );
}

export { AdminViewAll };
